import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Clock,
  Hourglass,
  ListFilter,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import StatusChip from "@/components/shared/StatusChip";
import StatCard from "@/components/shared/StatCard";
import SectionCard from "@/components/shared/SectionCard";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { IconAction } from "@/components/shared/IconAction";
import { formatDate } from "@/utils/formatters";
import { useStudentWorkLogsList, useDeleteStudentWorkLog } from "@/hooks/useStudentData";
import { TableSkeleton } from "@/components/shared/Skeletons";
import ActivityCalendar, { CalendarAsideCard } from "@/components/shared/ActivityCalendar";
import { toneForStatus } from "@/components/shared/EventChip";
import {
  latestLogs,
  relativeDay,
  workLogEvents,
  workLogLegend,
} from "@/components/shared/workLogCalendar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/student/work-logs/")({ component: WorkLogsList });

type WorkLogItem = {
  id: string | number;
  partnerName?: string;
  content?: string;
  description?: string;
  hours?: number;
  status?: string;
  date?: string;
};

function WorkLogsList() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showAllHistory, setShowAllHistory] = useState(false);

  const { data: logs = [], isLoading } = useStudentWorkLogsList();
  const deleteMutation = useDeleteStudentWorkLog();
  const [deleteTarget, setDeleteTarget] = useState<WorkLogItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const canEdit = (status?: string) =>
    status === "pending" || status === "changes_requested";

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(String(deleteTarget.id));
      setDeleteTarget(null);
    } catch (err: any) {
      setDeleteError(
        err?.response?.data?.error || err?.message || "Could not delete this work log.",
      );
    }
  };

  const filtered = logs.filter((l: WorkLogItem) => {
    const matchesStatus = statusFilter === "all" || l.status === statusFilter;
    const matchesSearch =
      (l.partnerName?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (l.description?.toLowerCase() || l.content?.toLowerCase() || "").includes(
        search.toLowerCase(),
      );
    return matchesStatus && matchesSearch;
  });

  const totalHours = logs.reduce((sum: number, log: WorkLogItem) => sum + (log.hours || 0), 0);
  const approvedCount = logs.filter((l: WorkLogItem) => l.status === "approved").length;
  const pendingCount = logs.filter((l: WorkLogItem) => l.status === "pending").length;
  const visibleHistory = useMemo(
    () => (showAllHistory ? filtered : filtered.slice(0, 3)),
    [filtered, showAllHistory],
  );

  const calendarEvents = useMemo(
    () =>
      workLogEvents(
        logs as WorkLogItem[],
        (l) => l.partnerName || "Work log",
        (l) => `/student/work-logs/${l.id}`,
      ),
    [logs],
  );

  if (isLoading) {
    return <TableSkeleton rows={5} columns={4} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Work logs"
        subtitle="Keep a clean record of daily tasks, approvals, and submitted hours."
        breadcrumbs={[{ label: "Dashboard", path: "/student/dashboard" }, { label: "Work Logs" }]}
        action={
          <Button asChild>
            <Link to="/student/work-logs/new">
              <Plus />
              Submit new log
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
        <StatCard label="Total hours" value={totalHours} icon={<Clock />} />
        <StatCard label="Approved" value={approvedCount} icon={<CheckCircle2 />} />
        <StatCard label="Pending" value={pendingCount} icon={<Hourglass />} />
        <StatCard label="Total entries" value={logs.length} icon={<ListFilter />} />
      </div>

      <ActivityCalendar
        title="Activity calendar"
        events={calendarEvents}
        legend={workLogLegend(logs as WorkLogItem[])}
        asideTitle="Recent submissions"
        aside={
          logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Logs you submit will show up here.</p>
          ) : (
            <div className="space-y-2.5">
              {latestLogs(logs as WorkLogItem[]).map((log) => (
                <CalendarAsideCard
                  key={log.id}
                  tone={log.status === "changes_requested" ? "lilac" : toneForStatus(log.status)}
                  title={log.partnerName || "Work log"}
                  meta={`${formatDate(log.date ?? "")} · ${log.hours ?? 0}h`}
                  badge={relativeDay(log.date)}
                  to={`/student/work-logs/${log.id}`}
                />
              ))}
            </div>
          )
        }
      />

      <SectionCard
        title="Submission history"
        description={`Showing ${filtered.length} of ${logs.length} logs`}
        noPadding
        actions={
          <>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by institution or task description"
              className="sm:w-72"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" aria-label="Status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
      >
        {filtered.length === 0 ? (
          <EmptyState
            bare
            title="No logs found"
            description="No logs found matching your filters."
          />
        ) : (
          <ul className="divide-y divide-border">
            {visibleHistory.map((log: WorkLogItem) => (
              <li
                key={log.id}
                className="grid gap-3 px-5 py-4 transition-colors hover:bg-muted/40 md:px-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span
                    className="flex size-10 shrink-0 items-center justify-center rounded-full bg-pastel-lavender text-pastel-lavender-ink"
                    aria-hidden
                  >
                    <Briefcase className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <h3 className="text-sm font-medium text-foreground">{log.partnerName}</h3>
                      <MetaInline label="Date" value={formatDate(log.date ?? "")} />
                      <MetaInline label="Hours" value={`${log.hours ?? 0}h`} strong />
                      <StatusChip status={log.status ?? ""} />
                    </div>
                    <p className="line-clamp-2 text-sm leading-6 text-muted-foreground">
                      {log.description}
                      {!log.description && log.content}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 pl-13 lg:pl-0">
                  {canEdit(log.status) && (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-foreground"
                            asChild
                          >
                            <Link
                              to={`/student/work-logs/edit/${log.id}` as never}
                              aria-label="Edit"
                            >
                              <Pencil />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit</TooltipContent>
                      </Tooltip>
                      <IconAction
                        label="Delete"
                        icon={<Trash2 />}
                        tone="destructive"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteTarget(log);
                        }}
                      />
                    </>
                  )}
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/student/work-logs/${log.id}` as never}>
                      Details
                      <ArrowRight />
                    </Link>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {filtered.length > 3 && (
          <div className="border-t border-border px-4 py-2 md:px-5">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setShowAllHistory((value) => !value)}
            >
              {showAllHistory ? "Show less" : `View all (${filtered.length})`}
            </Button>
          </div>
        )}
      </SectionCard>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete this work log?</DialogTitle>
            <DialogDescription>
              {`This will permanently remove the ${deleteTarget?.hours ?? 0}h entry${
                deleteTarget?.date ? ` for ${formatDate(deleteTarget.date)}` : ""
              }. Only a pending or changes-requested log can be deleted.`}
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription className="text-foreground">{deleteError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaInline({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-caption">
      <span className="text-muted-foreground">
        {label}
      </span>
      <span className={strong ? "text-foreground tabular-nums" : "text-body tabular-nums"}>
        {value}
      </span>
    </span>
  );
}
